import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad } from "@redotech/react-util/load";
import ArrowRightIcon from "@redotech/redo-icons/arrow-right_filled.svg";
import { useCampaign } from "@redotech/redo-marketplace-app-common/campaign-context";
import { RedoMarketplaceRpcClientContext } from "@redotech/redo-marketplace-app-common/rpc-client";
import { VendorAuthContext } from "@redotech/redo-marketplace-app-common/vendor-auth";
import { RedoSpinner } from "@redotech/redo-web/arbiter-components/loading/redo-spinner";
import { Breadcrumbs } from "@redotech/redo-web/breadcrumb";
import { Flex } from "@redotech/redo-web/flex";
import { ImageDisplayWithPlaceholder } from "@redotech/redo-web/image-display-with-placeholder";
import { Text } from "@redotech/redo-web/text";
import { memo, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { Brand } from "../types/brand";

interface BrandCardProps {
  brand: Brand;
  onBrandClick: (brandId: string) => void;
}

const BrandCard = memo(function BrandCard({
  brand,
  onBrandClick,
}: BrandCardProps) {
  const handleClick = useHandler(() => {
    onBrandClick(brand.name);
  });

  return (
    <div
      className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow cursor-pointer group"
      onClick={handleClick}
    >
      <Flex align="center" flex="1" justify="space-between">
        <Flex align="center" gap="md">
          {brand.logo && (
            <ImageDisplayWithPlaceholder
              alt={brand.name}
              className="object-cover"
              size="sm"
              src={brand.logo ?? ""}
            />
          )}
          <Flex dir="column" gap="xxs">
            <Text fontSize="md" fontWeight="semibold">
              {brand.name}
            </Text>
            {brand.description && (
              <Text color="tertiary" fontSize="xs">
                {brand.description}
              </Text>
            )}
            <Text color="tertiary" fontSize="xs">
              {brand.productCount} items available
            </Text>
          </Flex>
        </Flex>
        <ArrowRightIcon />
      </Flex>
    </div>
  );
});

export const BrandListing = memo(function BrandListing() {
  const navigate = useNavigate();
  const vendorAuth = useContext(VendorAuthContext);
  const { isInCampaign } = useCampaign();
  const marketplaceRpcClient = useRequiredContext(
    RedoMarketplaceRpcClientContext,
  );

  const vendorCountsResult = useLoad(async () => {
    if (!vendorAuth) {
      return null;
    }
    return await marketplaceRpcClient.getMarketplaceVendorCounts({
      marketplaceType: "REDO",
    });
  }, [marketplaceRpcClient, vendorAuth]);

  const handleBrandClick = useHandler((brandId: string) => {
    navigate(`/brands/${brandId}`);
  });

  const brands: Brand[] =
    vendorCountsResult.value?.vendorCounts.map(
      ({ vendor, count, image, category }) => ({
        name: vendor,
        description: category,
        logo: image?.url,
        productCount: count,
      }),
    ) || [];

  const totalBrands = brands.length;

  // If user is in a campaign, don't show brands page
  if (isInCampaign) {
    return null;
  }

  return (
    <Flex flexDirection="column" gap="lg">
      <Breadcrumbs size="sm" />

      <Flex align="center" justify="space-between">
        <Flex align="center" gap="sm">
          <Text fontSize="2xl" fontWeight="bold">
            All Brands
          </Text>
          {vendorCountsResult.pending && <RedoSpinner />}
          {vendorCountsResult.value && (
            <Text color="tertiary" fontSize="2xl">
              ({totalBrands})
            </Text>
          )}
        </Flex>
      </Flex>

      {vendorCountsResult.error && (
        <Text color="red">
          Error loading brands: {vendorCountsResult.error.message}
        </Text>
      )}

      {vendorCountsResult.value && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {brands.length === 0 ? (
            <Text color="gray">
              No brands available in the marketplace yet.
            </Text>
          ) : (
            brands.map((brand) => (
              <BrandCard
                brand={brand}
                key={brand.name}
                onBrandClick={handleBrandClick}
              />
            ))
          )}
        </div>
      )}
    </Flex>
  );
});
